import { ShipmentsListType } from '@shared-features/enums';
import { RouteConfig } from '../models/route-config.model';
import { CustomerServiceType } from '@shared-features/enums';
import { BreadcrumbLabels } from '@shared/enums/breadcrumb-labels.enum';

export class RoutesUtil {
  static Dashboards = new RouteConfig('dashboards', undefined, BreadcrumbLabels.Dashboard);
  static Dashboard = new RouteConfig('dashboard', RoutesUtil.Dashboards);
  static CustomDashboard = new RouteConfig<{ name: string }>('custom-dashboard/:name', RoutesUtil.Dashboards);
  static OrganizationDashboard = new RouteConfig('organization-dashboard', RoutesUtil.Dashboards);

  // shipments
  static Shipments = new RouteConfig('shipments', undefined, BreadcrumbLabels.Shipments);
  static ShipmentsList = new RouteConfig<{ type: ShipmentsListType }>(':type', RoutesUtil.Shipments);
  static MyShipments = new RouteConfig('my-shipments', RoutesUtil.Shipments);
  static MyShipmentsAdd = new RouteConfig('add', RoutesUtil.MyShipments);
  static ShipmentsAdd = new RouteConfig('add', RoutesUtil.Shipments);
  static ShipmentsView = new RouteConfig<{ id: number; type: ShipmentsListType }>(':type/view/:id', RoutesUtil.Shipments);
  static ShipmentsEditWithoutRange = new RouteConfig<{ id: number; type: ShipmentsListType }>(':type/edit/:id', RoutesUtil.Shipments);

  // Registered before ShipmentsList (':type') in shipments-routing.module.ts — otherwise
  // ':type' would match 'bulk-shipments' and render the shipments list instead.
  static BulkShipments = new RouteConfig<void, void>('bulk-shipments', RoutesUtil.Shipments);

  static ShipmentTemplate = new RouteConfig('shipments/template');
  static ShipmentTemplateCementAdd = new RouteConfig<void, { redirect?: string }>('cement/add', RoutesUtil.ShipmentTemplate);
  static ShipmentTemplateCementEdit = new RouteConfig<{ id: number }, { redirect?: string }>('cement/:id', RoutesUtil.ShipmentTemplate);
  
  static ShipmentTemplateBasicAdd = new RouteConfig<void, { redirect?: string }>('main/add', RoutesUtil.ShipmentTemplate);
  static ShipmentTemplateBasicEdit = new RouteConfig<{ id: number }, { redirect?: string }>('main/:id', RoutesUtil.ShipmentTemplate);

  // Order
  static Orders = new RouteConfig('orders', undefined, BreadcrumbLabels.Orders);
  static OrdersListPreview = new RouteConfig('preview', RoutesUtil.Orders);

  static ShipmentTemplateOrdersAdd = new RouteConfig<void, { redirect?: string }>('orders/add', RoutesUtil.ShipmentTemplate);

  /** Create shipments by uploading a filled sheet, read with the customer's saved mapping. */
  static ShipmentTemplateBulkImport = new RouteConfig<void, void>('import', RoutesUtil.ShipmentTemplate);

  // live tracking
  static LiveTracking = new RouteConfig('live-tracking/b2b', undefined, BreadcrumbLabels.LiveTracking);

  static LiveTrackingAvl = new RouteConfig<{ id: number }>('avl', RoutesUtil.LiveTracking);
  // Daleel Service is not working now
  static LiveTrackingDaleel = new RouteConfig('daleel', RoutesUtil.LiveTracking);
  static LiveTrackingDaleelShipment = new RouteConfig<{ id: number }>(':id', RoutesUtil.LiveTrackingDaleel);
  static LiveTrackingMobile = new RouteConfig('mobile', RoutesUtil.LiveTracking);
  static LiveTrackingTrackYourShipment = new RouteConfig('track', RoutesUtil.LiveTracking);
  static LiveTrackingShipment = new RouteConfig<{ id: number }>(':id', RoutesUtil.LiveTrackingTrackYourShipment);
  // customers
  static Customers = new RouteConfig('customers', undefined, BreadcrumbLabels.BusinessUnits);
  static CustomersProfile = new RouteConfig('profile', RoutesUtil.Customers);
  static CustomersAdd = new RouteConfig('Add', RoutesUtil.Customers);
  static CustomerInfo = new RouteConfig('customer-info', RoutesUtil.Customers);
  static CustomersView = new RouteConfig<{ id: number }>('view/:id', RoutesUtil.Customers);
  static CustomersViewWithTab = new RouteConfig<{ id: number; tabIndex: number }>('view/:id/:tabIndex', RoutesUtil.Customers);
  static CustomersUsersAdd = new RouteConfig<{ id: number }>(':id/users/add', RoutesUtil.Customers);
  static CustomersUsersView = new RouteConfig<{ customerId: number; userId: number }>(
    ':customerId/users/view/:userId',
    RoutesUtil.Customers
  );
  static CustomersReceiversAdd = new RouteConfig<{ id: number }>(':id/receivers/add', RoutesUtil.Customers);
  static CustomersReceiversView = new RouteConfig<{ customerId: number; receiverId: number }>(
    ':customerId/receivers/view/:receiverId',
    RoutesUtil.Customers
  );

  static CustomersTradeLanesAdd = new RouteConfig<{ customerId: number; contractId: number }>(
    ':customerId/contracts/:contractId/trade-lane/add',
    RoutesUtil.Customers
  );
  static CustomersTradeLanesEdit = new RouteConfig<{
    customerId: number;
    contractId: number;
    tradeLaneId: number;
  }>(':customerId/contracts/:contractId/trade-lane/edit/:tradeLaneId', RoutesUtil.Customers);

  static LocationCustomerView = new RouteConfig<{ id: number }>('location-customer/view/:id', RoutesUtil.Customers);
  static MyCustomers = new RouteConfig('my-customers', RoutesUtil.Customers, BreadcrumbLabels.CustomersMyCustomers);
  // `path` already spells out `my-customers/`, so the parent is `Customers` and not `MyCustomers`;
  // naming both would compose `/customers/my-customers/my-customers/add`.
  static MyCustomersAdd = new RouteConfig('my-customers/add', RoutesUtil.Customers);
  static CustomersVerify = new RouteConfig('my-customers/verify', RoutesUtil.Customers);
  static MyCustomersView = new RouteConfig<{ id: number }>('my-customers/view/:id', RoutesUtil.Customers);
  static MyWalletView = new RouteConfig('my-wallet', RoutesUtil.Customers);

  static Logs = new RouteConfig('logs');
  static LogsWcc = new RouteConfig('wcc', RoutesUtil.Logs);

  // fleet
  static Fleets = new RouteConfig('fleets', undefined, BreadcrumbLabels.Providers);
  static FleetInfo = new RouteConfig('company-info', RoutesUtil.Fleets);
  static FleetsAdd = new RouteConfig('add', RoutesUtil.Fleets);
  static FleetsView = new RouteConfig<{ id: number }>('view/:id', RoutesUtil.Fleets);
  static FleetsAddBulkDrivers = new RouteConfig<{ id: number }>(':id/add-bulk-drivers', RoutesUtil.Fleets);
  static FleetsAddBulkTrucks = new RouteConfig<{ id: number }>(':id/add-bulk-trucks', RoutesUtil.Fleets);
  static FleetsAddBulkDriversTrucks = new RouteConfig<{ id: number }>(':id/add-bulk-drivers-with-trucks', RoutesUtil.Fleets);
  static FleetsRequest = new RouteConfig<{ id: number }>('requests/:id', RoutesUtil.Fleets);
  static FleetsMadar = new RouteConfig<{ id: number }>('madar-providers', RoutesUtil.Fleets);
  static FleetsUsers = new RouteConfig('users', RoutesUtil.Fleets);
  static FleetsUsersAdd = new RouteConfig<{ id: number }>(':id/users/add', RoutesUtil.Fleets);
  static FleetsUsersView = new RouteConfig<{ fleetId: number; userId: number }>(':fleetId/users/:userId', RoutesUtil.Fleets);
  static FleetsUsersViewOnly = new RouteConfig<{ fleetId: number; userId: number }>(':fleetId/users/view-only/:userId', RoutesUtil.Fleets);
  static FleetsDrivers = new RouteConfig('drivers', RoutesUtil.Fleets);
  static FleetsDriversAdd = new RouteConfig<{ fleetName: string; id: number }>(':fleetName/:id/drivers/add', RoutesUtil.Fleets);

  static FleetsDriversView = new RouteConfig<{ id: number }>('drivers/view/:id', RoutesUtil.Fleets);
  static FleetsTrucks = new RouteConfig('trucks', RoutesUtil.Fleets);
  static FleetsTrucksAdd = new RouteConfig<{ fleetName: string; id: number }>(':fleetName/:id/trucks/add', RoutesUtil.Fleets);
  static FleetsTrucksView = new RouteConfig<{ id: number }>('trucks/view/:id', RoutesUtil.Fleets);
  static FleetsRentedTrucks = new RouteConfig('rented-trucks', RoutesUtil.Fleets);
  static FleetsTruckOperationReport = new RouteConfig<{ id: number }>('rented-trucks/:id/operation-report', RoutesUtil.Fleets);

  static FleetsTradeLanesAdd = new RouteConfig<{ fleetId: number; contractId: number }>(
    ':fleetId/contracts/:contractId/trade-lane/add',
    RoutesUtil.Fleets
  );
  static FleetsTradeLanesEdit = new RouteConfig<{
    fleetId: number;
    contractId: number;
    tradeLaneId: number;
  }>(':fleetId/contracts/:contractId/trade-lane/edit/:tradeLaneId', RoutesUtil.Fleets);

  static FleetsTrailersAdd = new RouteConfig<{ fleetName: string; fleetId: number }>(':fleetName/:fleetId/trailers/add', RoutesUtil.Fleets);
  static FleetsTrailersEdit = new RouteConfig<{ id: number }>('trailers/edit/:id', RoutesUtil.Fleets);
  static FleetsTrailersView = new RouteConfig<{ id: number }>('trailers/:id', RoutesUtil.Fleets);

  // Reports
  static Reports = new RouteConfig('reports', undefined, BreadcrumbLabels.Reports);
  static ReportsConsolidation = new RouteConfig('consolidation', RoutesUtil.Reports);
  static ReportsDrivers = new RouteConfig('drivers', RoutesUtil.Reports);
  static ReportsTrucks = new RouteConfig('trucks', RoutesUtil.Reports);
  static ReportsTrucksView = new RouteConfig<{ id: number }>('trucks/:id', RoutesUtil.Reports);
  static ReportsG2gIntegration = new RouteConfig('g2g-integration', RoutesUtil.Reports);
  static ReportsG2G = new RouteConfig('G2G', RoutesUtil.Reports);
  static ReportsTruckUtilization = new RouteConfig('truck-utilization', RoutesUtil.Reports);

  // Users
  static Users = new RouteConfig('users', undefined, BreadcrumbLabels.Users);
  static UsersAdd = new RouteConfig('add', RoutesUtil.Users);
  static UsersViewEdit = new RouteConfig<{ userId: number }>(':userId', RoutesUtil.Users);

  // invoices
  static Invoices = new RouteConfig('invoices', undefined, BreadcrumbLabels.Invoices);
  static CustomerInvoices = new RouteConfig('customer', RoutesUtil.Invoices);
  static InvoicesFleet = new RouteConfig('fleet', RoutesUtil.Invoices);
  static InvoicesFleetCreate = new RouteConfig('fleet/create', RoutesUtil.Invoices);

  static InvoicesDeliveryNotes = new RouteConfig<{ invoiceId: number }>('delivery-notes/:invoiceId', RoutesUtil.Invoices);

  static FleetInoviceDetails = new RouteConfig<{ id: number }>('invoice/:id', RoutesUtil.Invoices);

  // Transactions
  static Transactions = new RouteConfig('transactions', undefined, BreadcrumbLabels.Transactions);
  static TransactionsList = new RouteConfig('list', RoutesUtil.Transactions);

  // delivery notes
  static DeliveryNotes = new RouteConfig('delivery-notes');

  // Receivers
  static Receivers = new RouteConfig('receivers', undefined, BreadcrumbLabels.Receivers);
  static ReceiversAdd = new RouteConfig('add', RoutesUtil.Receivers);
  static ReceiversView = new RouteConfig<{ receiverId: number }>('view/:receiverId', RoutesUtil.Receivers);

  // Lead
  static Leads = new RouteConfig('leads');
  static LeadsDriversAdd = new RouteConfig(':fleetName/:fleetId/:leadMobileNumber', RoutesUtil.Leads);

  // Organization
  static Organizations = new RouteConfig('organizations');
  static OrganizationsAdd = new RouteConfig('add', RoutesUtil.Organizations);
  static OrganizationsView = new RouteConfig<{ organizationId: number }>('view/:organizationId', RoutesUtil.Organizations);

  // Settings
  static Settings = new RouteConfig('settings', undefined, BreadcrumbLabels.Settings);

  static TransportationPayments = new RouteConfig('transportation-payments', undefined, BreadcrumbLabels.TransportationPayments);
  static TransportationPaymentsView = new RouteConfig<{ invoiceId: number }>('view/:invoiceId', RoutesUtil.TransportationPayments);

  static Auth = new RouteConfig('auth');
  static AuthLogin = new RouteConfig('login', RoutesUtil.Auth);
  static AuthVerifyEmail = new RouteConfig<{ token: string }>('verify-email/:token', RoutesUtil.Auth);
  static AuthResetPassword = new RouteConfig<{ token: string }>('reset-password/:token', RoutesUtil.Auth);
  static AuthResetPasswordBy = new RouteConfig('reset-password-by', RoutesUtil.Auth);
  static AuthOTP = new RouteConfig<{ mobileNumber: string }>('otp/:mobileNumber', RoutesUtil.Auth);

  static Home = RoutesUtil.Dashboard;

  static AccessDenied = new RouteConfig('access-denied');
  static NotFound = new RouteConfig('not-found');

  static Public = new RouteConfig('public');
  static PublicRegistration = new RouteConfig('registration', RoutesUtil.Public);
  // The customer sign-up wizard: the parent is the shell + mobile-number step, and each
  // later step is its own route so refresh and Back land back on the same step.
  static PublicCustomerRegistration = new RouteConfig('customer', RoutesUtil.PublicRegistration);
  static PublicCustomerRegistrationOtp = new RouteConfig('otp', RoutesUtil.PublicCustomerRegistration);
  static PublicCustomerRegistrationProfile = new RouteConfig('profile', RoutesUtil.PublicCustomerRegistration);
  static PublicCustomerRegistrationBusiness = new RouteConfig('business', RoutesUtil.PublicCustomerRegistration);
  static PublicFleetRegistration = new RouteConfig('fleet', RoutesUtil.PublicRegistration);
  static TermsAndConditions = new RouteConfig('madar-terms-and-conditions', RoutesUtil.PublicRegistration);
  static PublicLocation = new RouteConfig('location', RoutesUtil.Public);
  static PublicMobileStoreRedirect = new RouteConfig('mobile-store-redirect', RoutesUtil.Public);

  static Booking = new RouteConfig('booking');
  static BookingShipmentDetails = new RouteConfig<{ id: number }>('shipment-details/:id', RoutesUtil.Booking);
  static BookingPaymentStatus = new RouteConfig('payment-status', RoutesUtil.Booking);

  // Support
  static Support = new RouteConfig('customer-support', undefined, BreadcrumbLabels.CustomersSupport);
  static SupportAddTicket = new RouteConfig('add-ticket', RoutesUtil.Support);
  static SupportTickets = new RouteConfig('tickets', RoutesUtil.Support);

  // Trip Planning
  static TripPlanning = new RouteConfig('trip-planning', undefined, BreadcrumbLabels.TripPlanning);
  static TripPlanningFleets = new RouteConfig('fleets', RoutesUtil.TripPlanning);
  static TripPlanningPerId = new RouteConfig<{ id: number; name: string }>('fleets/:id/:name', RoutesUtil.TripPlanning);

  // contracts
  // Contracts
  static Contracts = new RouteConfig('contracts', undefined, BreadcrumbLabels.Contracts);
  static ContractsOpsList = new RouteConfig(CustomerServiceType.Service, RoutesUtil.Contracts);
  static ContractsSaasList = new RouteConfig(CustomerServiceType.SAAS, RoutesUtil.Contracts);
  static ContractsView = new RouteConfig<{ id: number; type: CustomerServiceType }>(':type/view/:id', RoutesUtil.Contracts);
  static ContractsEdit = new RouteConfig<{ id: number; type: CustomerServiceType }>(':type/edit/:id', RoutesUtil.Contracts);
  static ContractsAdd = new RouteConfig<{ type: CustomerServiceType }>(':type/add', RoutesUtil.Contracts);
  static ContractsTradeLanesAdd = new RouteConfig<{ contractId: number; type: CustomerServiceType }>(
    ':type/trade-lanes/:contractId/add',
    RoutesUtil.Contracts
  );
  static ContractsTradeLanesEdit = new RouteConfig<{ contractId: number; type: CustomerServiceType; id: number }>(
    ':type/trade-lanes/:contractId/edit/:id',
    RoutesUtil.Contracts
  );

  static Payment = new RouteConfig('payment');
  static PaymentStatus = new RouteConfig('status', RoutesUtil.Payment);

  // My Tickets
  // static CustomersContractsAdd = new RouteConfig<{ id: number, name: string }>(
  //   ':id/:name/contracts/add', RoutesUtil.Customers);
  // static CustomersContractsEdit = new RouteConfig<{
  //   type: string;
  //   contractId: number;
  //   customerId: number;
  // }>(':customerId/contracts/:type/edit/:contractId', RoutesUtil.Customers);
  // static GlobalSpecificContractDetails = new RouteConfig<{
  //   id: number;
  //   name: string;
  //   fleetName: string;
  //   contractId: number;
  // }>(':id/:name/:fleetName/:contractId/global-specific-contract/details', RoutesUtil.Customers);
  //
  // static FleetsContractsAdd = new RouteConfig<{ id: number, name: string }>(
  //   ':id/:name/contracts/add', RoutesUtil.Fleets);
  // static FleetsContractsEdit = new RouteConfig<{
  //   type: string;
  //   contractId: number;
  //   fleetId: number;
  // }>(':fleetId/contracts/:type/edit/:contractId', RoutesUtil.Fleets);
  // static FleetsGlobalSpecificContractDetails = new RouteConfig<{
  //   id: number;
  //   name: string;
  //   fleetName: string;
  //   contractId: number;
  // }>(':id/:name/:fleetName/:contractId/global-specific-contract/details', RoutesUtil.Fleets);

  // guest
  static Guest = new RouteConfig('guest');
}
